import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import SalonCard from '../../components/SalonCard/SalonCard';
import salonService from '../../services/salonService';
import './SalonList.css';

const SORTS = [
  { id: 'recent', label: 'Nouveautés' },
  { id: 'rating', label: 'Mieux notés' },
  { id: 'name', label: 'A → Z' },
];

const SalonList = () => {
  const [searchParams] = useSearchParams();
  const [salons, setSalons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [city, setCity] = useState(searchParams.get('city') || '');
  const [specialtyId, setSpecialtyId] = useState(searchParams.get('specialty') || '');
  const [specialtyName, setSpecialtyName] = useState(searchParams.get('name') || '');
  const [serviceType, setServiceType] = useState('all');
  const [sort, setSort] = useState('recent');

  useEffect(() => {
    setSearch(searchParams.get('q') || '');
    setCity(searchParams.get('city') || '');
    setSpecialtyId(searchParams.get('specialty') || '');
    setSpecialtyName(searchParams.get('name') || '');
  }, [searchParams]);

  useEffect(() => {
    fetchSalons();
  }, []);

  const fetchSalons = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await salonService.getAllSalons();
      setSalons(data || []);
    } catch (err) {
      console.error('Salons:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let list = [...salons];

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((s) =>
        [s.name, s.address, s.city].filter(Boolean).some((v) => v.toLowerCase().includes(q))
      );
    }

    if (city.trim()) {
      const q = city.trim().toLowerCase();
      list = list.filter((s) => (s.city || '').toLowerCase().includes(q));
    }

    if (specialtyId) {
      const sid = String(specialtyId);
      list = list.filter((s) =>
        Array.isArray(s.specialties) &&
        s.specialties.some((sp) => String(sp.id || sp) === sid || String(sp.name || '') === specialtyName)
      );
    } else if (specialtyName) {
      const n = specialtyName.toLowerCase();
      list = list.filter((s) =>
        Array.isArray(s.specialties) &&
        s.specialties.some((sp) => String(sp.name || sp).toLowerCase().includes(n))
      );
    }

    if (serviceType !== 'all') {
      list = list.filter((s) => s.service_type === serviceType);
    }

    if (sort === 'rating') {
      list.sort(
        (a, b) =>
          parseFloat(b.average_rating || 0) - parseFloat(a.average_rating || 0)
      );
    } else if (sort === 'name') {
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else {
      list.sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
    }

    return list;
  }, [salons, search, city, specialtyId, specialtyName, serviceType, sort]);

  return (
    <div className="salon-list">
      {/* ═══════ HERO ═══════ */}
      <section className="sl-hero">
        <div className="sl-hero-glow sl-hero-glow-1" />
        <div className="sl-hero-glow sl-hero-glow-2" />
        <div className="section-container sl-hero-inner">
          <span className="kicker sh-reveal">
            {specialtyName ? specialtyName : 'Tous nos salons'}
          </span>
          <h1 className="sl-title sh-reveal sh-stagger-1">
            {specialtyName ? (
              <>
                Salons <span className="sl-title-accent">{specialtyName}</span>
              </>
            ) : (
              <>
                Découvrez les <span className="sl-title-accent">plus beaux salons</span>
                <br />
                de Dakar
              </>
            )}
          </h1>
          <p className="sl-subtitle sh-reveal sh-stagger-2">
            Filtrez, comparez et réservez en quelques clics.
          </p>

          <div className="sl-search-card sh-reveal-scale sh-stagger-3">
            <div className="sl-search-field">
              <span className="sl-icon">🔎</span>
              <input
                type="text"
                placeholder="Rechercher un salon, un soin..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="sl-search-divider" />
            <div className="sl-search-field">
              <span className="sl-icon">📍</span>
              <input
                type="text"
                placeholder="Quartier, ville..."
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ TOOLBAR ═══════ */}
      <section className="sl-toolbar">
        <div className="section-container sl-toolbar-inner">
          <div className="sl-chips">
            <button
              className={`sl-chip ${serviceType === 'all' ? 'active' : ''}`}
              onClick={() => setServiceType('all')}
            >
              Tous
            </button>
            <button
              className={`sl-chip ${serviceType === 'physical' ? 'active' : ''}`}
              onClick={() => setServiceType('physical')}
            >
              Au salon
            </button>
            <button
              className={`sl-chip ${serviceType === 'home' ? 'active' : ''}`}
              onClick={() => setServiceType('home')}
            >
              À domicile
            </button>
            <button
              className={`sl-chip ${serviceType === 'both' ? 'active' : ''}`}
              onClick={() => setServiceType('both')}
            >
              Les deux
            </button>
          </div>

          <div className="sl-sort">
            <label>Trier par</label>
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              {SORTS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* ═══════ RESULTS ═══════ */}
      <section className="sl-results">
        <div className="section-container">
          <div className="sl-results-head">
            <h2 className="section-title">
              {loading
                ? 'Chargement…'
                : `${filtered.length} salon${filtered.length > 1 ? 's' : ''} ${
                    filtered.length > 1 ? 'trouvés' : 'trouvé'
                  }`}
            </h2>
            {!loading && (
              <p className="section-subtitle">
                Triés par {SORTS.find((s) => s.id === sort)?.label.toLowerCase()}
              </p>
            )}
          </div>

          {error ? (
            <div className="sl-empty">
              <span className="empty-emoji">⚠️</span>
              <p className="empty-title">Oups, impossible de charger les salons</p>
              <p className="empty-sub">{error}</p>
              <button className="sl-retry-btn" onClick={fetchSalons}>
                Réessayer
              </button>
            </div>
          ) : loading ? (
            <div className="sl-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="sl-skeleton" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="sl-empty">
              <span className="empty-emoji">🫧</span>
              <p className="empty-title">Aucun salon ne correspond</p>
              <p className="empty-sub">Modifiez vos filtres pour voir plus de résultats.</p>
            </div>
          ) : (
            <div className="sl-grid">
              {filtered.map((salon, i) => (
                <div
                  key={salon.id}
                  className={`sh-reveal sh-stagger-${(i % 6) + 1}`}
                >
                  <SalonCard salon={salon} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SalonList;
